import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notifyError } from '../ui/Toasts.js'
import { CustomerForm } from './Customers.js'
import { api, type BueroKunde, type KundenBlatt, type KundenFilter } from '../net/apiClient.js'
import { PdfAnsicht } from '../ui/PdfAnsicht.js'

/**
 * Kunden im Büro.
 *
 * Der Unterschied zur Auswahlliste auf dem Gerät steht rechts: offener
 * Betrag, Umsatz, letzte Rechnung. Danach wird im Büro gesucht – «wer
 * schuldet uns noch etwas» und «wann hatten wir den zuletzt». Ein Name mit
 * Adresse darunter beantwortet keine dieser Fragen.
 *
 * Das Kundenblatt dahinter ist absichtlich kein CRM. Es zeigt, was an diesem
 * Kunden hängt, und lässt von dort aus weiterarbeiten: eine Rechnung
 * schreiben, eine Offerte schreiben, einen Beleg öffnen. Eine Detailansicht,
 * aus der kein Weg hinausführt, ist eine Sackgasse mit Daten darin.
 */

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

const FILTER: Array<{ id: KundenFilter; label: string }> = [
  { id: 'aktiv', label: 'Aktiv' },
  { id: 'offen', label: 'Mit offenem Betrag' },
  { id: 'archiviert', label: 'Archiviert' },
  { id: 'alle', label: 'Alle' }
]

export function OfficeCustomers({
  session,
  aufBeleg,
  aufSchnellstart,
  aufProjekt
}: {
  session: SessionUser
  aufBeleg?: (id: string) => void
  /** Express-Rechnung oder Express-Offerte mit vorbelegtem Kunden öffnen. */
  aufSchnellstart?: (art: 'rechnung' | 'offerte', vorgabe: Vorgabe) => void
  aufProjekt?: (id: string) => void
}): JSX.Element {
  const [filter, setFilter] = useState<KundenFilter>('aktiv')
  const [suche, setSuche] = useState('')
  const [items, setItems] = useState<BueroKunde[]>([])
  const [zaehler, setZaehler] = useState<Record<string, number>>({})
  const [summe, setSumme] = useState<{ offenRp: number; umsatzRp: number } | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [offen, setOffen] = useState<string | null>(null)
  const [neu, setNeu] = useState(false)

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const r = await api.officeCustomers(filter, suche)
      setItems(r.items)
      setZaehler(r.zaehler as unknown as Record<string, number>)
      setSumme(r.summe as { offenRp: number; umsatzRp: number })
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [filter, suche])

  useEffect(() => {
    void laden()
  }, [laden])

  if (offen) {
    return (
      <Kundenblatt
        session={session}
        id={offen}
        aufBeleg={aufBeleg}
        aufProjekt={aufProjekt}
        aufSchnellstart={aufSchnellstart}
        zurueck={() => {
          setOffen(null)
          void laden()
        }}
      />
    )
  }

  return (
    <>
      <OfficeHead
        titel="Kunden"
        unter={summe ? `CHF ${formatChf(summe.offenRp)} offen bei allen Kunden` : 'Wird geladen …'}
      >
        <button className="o-btn o-btn-primary" id="btn-neuer-kunde" onClick={() => setNeu(true)}>
          Neuer Kunde
        </button>
      </OfficeHead>

      {neu && (
        <CustomerForm
          session={session}
          record={null}
          onDone={() => {
            setNeu(false)
            void laden()
          }}
        />
      )}

      <div className="o-card">
        <div className="o-pad">
          <div className="o-suchzeile">
            <div className="o-filter" id="kundenfilter">
              {FILTER.map((f) => (
                <button
                  key={f.id}
                  id={`kfilter-${f.id}`}
                  className={f.id === filter ? 'o-chip aktiv' : 'o-chip'}
                  aria-pressed={f.id === filter}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span className="o-chip-zahl">{zaehler[f.id] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="o-field o-feldsuche">
              <label htmlFor="kunde-suche">Suche</label>
              <input
                id="kunde-suche"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Name, Ort, Strasse, Telefon oder E-Mail"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="o-card" id="kundenliste">
        <div className="o-card-head">
          <h3>
            {FILTER.find((f) => f.id === filter)?.label}{' '}
            <span className="o-badge o-badge-accent">{items.length}</span>
          </h3>
          <span className="o-soft">
            Offener Betrag und Umsatz aus den Belegen gerechnet, nicht gespeichert.
          </span>
        </div>

        {laedt ? (
          <div className="o-leer">Wird geladen …</div>
        ) : items.length === 0 ? (
          <div className="o-leer" id="kunden-leer">
            {suche.trim() ? (
              <>
                <strong>Kein Treffer.</strong>
                Gesucht wird über Name, Ort, Strasse, Telefon und E-Mail.
              </>
            ) : (
              <>
                <strong>Nichts in dieser Auswahl.</strong>
                Mit «Neuer Kunde» oben rechts entsteht der erste.
              </>
            )}
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ width: 190 }}>Ort</th>
                  <th style={{ width: 170 }}>Kontakt</th>
                  <th className="o-num" style={{ width: 90 }}>
                    Projekte
                  </th>
                  <th className="o-num" style={{ width: 115 }}>
                    Umsatz
                  </th>
                  <th className="o-num" style={{ width: 115 }}>
                    Offen
                  </th>
                  <th style={{ width: 175 }}>Letzte Rechnung</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <tr
                    key={k.id}
                    id={`kunde-${k.id}`}
                    className={k.active ? 'klickbar' : 'klickbar inaktiv'}
                    onClick={() => setOffen(k.id)}
                  >
                    <td>
                      {k.name}
                      {!k.active && (
                        <span className="o-badge o-badge-neutral" style={{ marginLeft: 8 }}>
                          archiviert
                        </span>
                      )}
                      {k.oneOff && (
                        <span className="o-badge o-badge-neutral" style={{ marginLeft: 8 }}>
                          Einmal-Adresse
                        </span>
                      )}
                    </td>
                    <td className="o-soft">{[k.zip, k.city].filter(Boolean).join(' ') || '–'}</td>
                    <td className="o-soft">{k.phone || k.email || k.contact || '–'}</td>
                    <td className="o-num">{k.projekte || '–'}</td>
                    <td className="o-num">{k.umsatzRp > 0 ? formatChf(k.umsatzRp) : '–'}</td>
                    <td className="o-num">
                      {k.offenRp > 0 ? <strong>{formatChf(k.offenRp)}</strong> : '–'}
                    </td>
                    <td className="o-soft">
                      {k.letzteNummer ? (
                        <>
                          <span className="o-mono">{k.letzteNummer}</span>
                          <div>{datum(k.letztesDatum)}</div>
                        </>
                      ) : (
                        'noch keine'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------

export interface Vorgabe {
  customerId: string
  customerName?: string
  projectId?: string
  projectName?: string
  objectStreet?: string | null
  objectZip?: string | null
  objectCity?: string | null
}

function Kundenblatt({
  session,
  id,
  zurueck,
  aufBeleg,
  aufProjekt,
  aufSchnellstart
}: {
  session: SessionUser
  id: string
  zurueck: () => void
  aufBeleg?: (id: string) => void
  aufProjekt?: (id: string) => void
  aufSchnellstart?: (art: 'rechnung' | 'offerte', vorgabe: Vorgabe) => void
}): JSX.Element {
  const [daten, setDaten] = useState<KundenBlatt | null>(null)
  const [bearbeiten, setBearbeiten] = useState(false)

  const laden = useCallback(async () => {
    try {
      setDaten(await api.officeCustomer(id))
    } catch (err) {
      notifyError(err)
    }
  }, [id])

  useEffect(() => {
    void laden()
  }, [laden])

  if (!daten) {
    return (
      <>
        <button className="o-zurueck" id="btn-kunde-zurueck" onClick={zurueck}>
          ← Kunden
        </button>
        <div className="o-card">
          <div className="o-leer">Wird geladen …</div>
        </div>
      </>
    )
  }

  const k = daten.kunde
  const vorgabe: Vorgabe = {
    customerId: k.id,
    customerName: k.name,
    objectStreet: k.street,
    objectZip: k.zip,
    objectCity: k.city
  }

  return (
    <>
      <button className="o-zurueck" id="btn-kunde-zurueck" onClick={zurueck}>
        ← Kunden
      </button>

      <OfficeHead
        titel={k.name}
        unter={[k.street, [k.zip, k.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
      >
        {/*
          Der Schnellstart ist der Grund, warum dieses Blatt existiert. Wer
          einen Kunden aufschlägt, will in neun von zehn Fällen etwas für ihn
          schreiben – und nicht danach im Menü suchen und den Namen ein
          zweites Mal eintippen.
        */}
        <button
          className="o-btn o-btn-primary"
          id="btn-kunde-rechnung"
          onClick={() => aufSchnellstart?.('rechnung', vorgabe)}
        >
          Rechnung schreiben
        </button>
        <button
          className="o-btn"
          id="btn-kunde-offerte"
          onClick={() => aufSchnellstart?.('offerte', vorgabe)}
        >
          Offerte schreiben
        </button>
        <button
          className="o-btn o-btn-ghost"
          id="btn-kunde-bearbeiten"
          onClick={() => setBearbeiten((v) => !v)}
        >
          {bearbeiten ? 'Bearbeiten schliessen' : 'Stammdaten bearbeiten'}
        </button>
      </OfficeHead>

      {bearbeiten && (
        <CustomerForm
          session={session}
          record={k as never}
          onDone={() => {
            setBearbeiten(false)
            void laden()
          }}
        />
      )}

      <div className="o-blatt">
        <div className="o-card">
          <div className="o-card-head">
            <h3>Stammdaten</h3>
            {!k.active && <span className="o-badge o-badge-neutral">archiviert</span>}
          </div>
          <div className="o-pad">
            <dl className="o-werte">
              <dt>Ansprechperson</dt>
              <dd>{k.contact || '–'}</dd>
              <dt>Adresse</dt>
              <dd>
                {k.street || '–'}
                <div>{[k.zip, k.city].filter(Boolean).join(' ')}</div>
              </dd>
              <dt>Telefon</dt>
              <dd>{k.phone || '–'}</dd>
              <dt>E-Mail</dt>
              <dd>{k.email || '–'}</dd>
              <dt>Notizen</dt>
              <dd>{k.notes || '–'}</dd>
            </dl>
            {!k.active && (
              <p className="o-soft" style={{ marginTop: 16 }}>
                Archiviert heisst: aus den Auswahllisten verschwunden, überall sonst unverändert
                sichtbar. Bestehende Belege und Projekte bleiben, wie sie sind.
              </p>
            )}
          </div>
        </div>

        <div className="o-card">
          <div className="o-card-head">
            <h3>Stand</h3>
          </div>
          <div className="o-pad">
            <div className="o-summe-zeile">
              <span>Offen</span>
              <strong id="kunde-offen">CHF {formatChf(daten.summe.offenRp)}</strong>
            </div>
            <div className="o-summe-zeile">
              <span>Umsatz</span>
              <span>CHF {formatChf(daten.summe.umsatzRp)}</span>
            </div>
            <div className="o-summe-zeile">
              <span>Rechnungen</span>
              <span>{daten.summe.rechnungen}</span>
            </div>
            <div className="o-summe-zeile">
              <span>Offerten</span>
              <span>{daten.summe.offerten}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="o-card" id="kunde-projekte">
        <div className="o-card-head">
          <h3>
            Projekte <span className="o-badge o-badge-accent">{daten.projekte.length}</span>
          </h3>
        </div>
        {daten.projekte.length === 0 ? (
          <div className="o-leer">
            <strong>Kein Projekt.</strong>
            Eine Rechnung braucht keines – das Projekt lässt sich in der Express-Rechnung auch als
            Text eintragen.
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th>Projekt</th>
                  <th style={{ width: 220 }}>Ort</th>
                  <th style={{ width: 140 }}>Zustand</th>
                  <th className="o-num" style={{ width: 110 }}>
                    Rapporte
                  </th>
                  <th style={{ width: 300 }} />
                </tr>
              </thead>
              <tbody>
                {daten.projekte.map((p) => (
                  <tr key={p.id} id={`kprojekt-${p.id}`}>
                    <td>{p.name}</td>
                    <td className="o-soft">{[p.zip, p.city].filter(Boolean).join(' ') || '–'}</td>
                    <td>
                      <span className="o-badge o-badge-neutral">{p.status}</span>
                    </td>
                    <td className="o-num">{p.rapporte || '–'}</td>
                    <td>
                      <div className="office-actions">
                        <button
                          className="o-btn o-btn-ghost"
                          id={`kprojekt-oeffnen-${p.id}`}
                          onClick={() => aufProjekt?.(p.id)}
                        >
                          Projekt öffnen
                        </button>
                        <button
                          className="o-btn o-btn-ghost"
                          id={`kprojekt-rechnung-${p.id}`}
                          onClick={() =>
                            aufSchnellstart?.('rechnung', {
                              ...vorgabe,
                              projectId: p.id,
                              projectName: p.name,
                              objectStreet: p.street,
                              objectZip: p.zip,
                              objectCity: p.city
                            })
                          }
                        >
                          Rechnung
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Belegtabelle
        titel="Belege"
        belege={daten.belege}
        aufBeleg={aufBeleg}
        leer="Für diesen Kunden gibt es noch keinen Beleg."
      />
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Rechnungen und Offerten eines Kunden oder einer Baustelle in einer Tabelle.
 *
 * Bewusst gemeinsam und nicht zwei Kästen: am Telefon fragt der Kunde nach
 * «dem Papier von Ihnen», und ob das eine Offerte oder eine Rechnung war,
 * weiss er selten. Die Spalte «Art» sagt es in einem Blick.
 */
export function Belegtabelle({
  titel,
  belege,
  aufBeleg,
  leer
}: {
  titel: string
  belege: Array<{
    id: string
    type: string
    number: string | null
    status: string
    issueDate: string | null
    totalRp: number
    offenRp: number
    zustand?: string | null
    ueberfaellig?: boolean
    hasPdf: boolean
    projectName?: string | null
  }>
  aufBeleg?: (id: string) => void
  leer: string
}): JSX.Element {
  /* Das PDF bleibt in der App – siehe ui/PdfAnsicht.tsx. */
  const [pdfAnsicht, setPdfAnsicht] = useState<{ url: string; titel: string } | null>(null)
  return (
    <div className="o-card" id="belegtabelle">
      {pdfAnsicht && (
        <PdfAnsicht
          url={pdfAnsicht.url}
          titel={pdfAnsicht.titel}
          aufZu={() => setPdfAnsicht(null)}
        />
      )}
      <div className="o-card-head">
        <h3>
          {titel} <span className="o-badge o-badge-accent">{belege.length}</span>
        </h3>
      </div>
      {belege.length === 0 ? (
        <div className="o-leer">{leer}</div>
      ) : (
        <div className="o-tabelle">
          <table className="o-data">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Art</th>
                <th style={{ width: 130 }}>Nummer</th>
                <th style={{ width: 110 }}>Datum</th>
                <th>Projekt</th>
                <th className="o-num" style={{ width: 115 }}>
                  Total
                </th>
                <th className="o-num" style={{ width: 115 }}>
                  Offen
                </th>
                <th style={{ width: 130 }}>Zustand</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {belege.map((d) => (
                <tr
                  key={d.id}
                  id={`kbeleg-${d.id}`}
                  className={d.status === 'storniert' ? 'inaktiv' : undefined}
                >
                  <td>{d.type === 'offerte' ? 'Offerte' : 'Rechnung'}</td>
                  <td className="o-mono">
                    {d.number ?? <span className="o-badge o-badge-neutral">Entwurf</span>}
                  </td>
                  <td>
                    {d.issueDate
                      ? new Date(`${d.issueDate}T00:00:00`).toLocaleDateString('de-CH')
                      : '–'}
                  </td>
                  <td className="o-soft">{d.projectName ?? '–'}</td>
                  <td className="o-num">{formatChf(d.totalRp)}</td>
                  <td className="o-num">
                    {d.type === 'rechnung' && d.offenRp > 0 ? formatChf(d.offenRp) : '–'}
                  </td>
                  <td>
                    <span
                      className={`o-badge ${
                        d.status === 'storniert'
                          ? 'o-badge-neutral'
                          : d.ueberfaellig
                            ? 'o-badge-alert'
                            : 'o-badge-neutral'
                      }`}
                    >
                      {d.type === 'offerte' ? (d.zustand ?? d.status) : d.status}
                    </span>
                  </td>
                  <td>
                    <div className="office-actions">
                      <button
                        className="o-btn o-btn-ghost"
                        id={`kbeleg-oeffnen-${d.id}`}
                        onClick={() => aufBeleg?.(d.id)}
                      >
                        Öffnen
                      </button>
                      {d.hasPdf && (
                        <button
                          type="button"
                          className="o-btn o-btn-ghost"
                          id={`kbeleg-pdf-${d.id}`}
                          onClick={() =>
                            setPdfAnsicht({
                              url: `/documents/${d.id}/pdf`,
                              titel: d.number ?? 'Beleg'
                            })
                          }
                        >
                          PDF
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
